/**
 * useRecordingWsStore — reactive recording state per hearing.
 *
 * The recording indicator in HearingRoomView reads this store to reflect
 * real-time state (recording / paused / stopped / error) without polling.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

export type RecordingWsState = 'stopped' | 'recording' | 'paused' | 'error'

export const useRecordingWsStore = defineStore('wsRecording', () => {
  const byHearingId = ref<Record<string, RecordingWsState>>({})
  const lastError = ref<Record<string, string>>({})

  function setState(hearingId: string, state: RecordingWsState, errorMsg?: string) {
    byHearingId.value = { ...byHearingId.value, [hearingId]: state }
    if (state === 'error' && errorMsg) {
      lastError.value = { ...lastError.value, [hearingId]: errorMsg }
    }
  }

  function get(hearingId: string): RecordingWsState {
    return byHearingId.value[hearingId] || 'stopped'
  }

  function clear(hearingId: string) {
    const next = { ...byHearingId.value }
    delete next[hearingId]
    byHearingId.value = next
    const nextErr = { ...lastError.value }
    delete nextErr[hearingId]
    lastError.value = nextErr
  }

  function reset() {
    byHearingId.value = {}
    lastError.value = {}
  }

  return {
    byHearingId,
    lastError,
    setState,
    get,
    clear,
    reset,
  }
})
